import { useEffect, useState } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from "motion/react";
import Sidebar from './components/Sidebar';
import Search from './components/Search';
import Carousel from './components/Carousel';
import CardCarousel from './components/CardCarousel';
import { useAppStore } from './stores/useAppStore';
import Logo from './components/Logo';
import IconSearch from './components/IconSearch';
import IconClose from './components/IconClose';
import ErrorBoundary from './components/ErrorBoundary';
import { useInactivityDetector } from './hooks/useInactivityDetector';
import { useDoubleClick } from './hooks/useDoubleClick';

import Home from './pages/Home';
import PageCiscoSpeakingSessions from './pages/PageCiscoSpeakingSessions';
import { CAROUSEL_TYPES } from './constants/carouselTypes';
import CardSpeaker from './components/CardSpeaker';
import CardBigDemo from './components/CardBigDemo';
import PageKeyDemos from './pages/PageKeyDemos';
import CardKeyDemoBig from './components/CardKeyDemoBig';
import PageOutcomeEnterprise from './pages/PageOutcomeEnterprise';
import PageOutcomeEnterpriseSubpages from './pages/PageOutcomeEnterpriseSubpages';
import PageCustomerStories from './pages/PageCustomerStories';
import PageEngagementZone from './pages/PageEngagementZone';
import Landing from './components/Landing';

const MotionIconSearch = motion.create(IconSearch);
const MotionIconClose = motion.create(IconClose);
const MotionLogo = motion.create(Logo);
const MotionButton = motion.create('button');

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen: carouselIsOpen, currentIndex: carouselIndex, type: carouselType, closeCarousel } = useAppStore((state) => state.carousel);
  const { content, resetMapOptions, menuItems, isLoading, loadContent } = useAppStore();
  const { fullScreen, toggleFullScreen, menuActiveKey, setMenuActiveKey, setCardTouchPos } = useAppStore();
  const [ prevLocation, setPrevLocation ] = useState(null);
  const { searchIsOpen, setSearchIsOpen } = useAppStore();
  const [ title, setTitle ] = useState("");
  const [ subtitle, setSubtitle ] = useState("");
  const [ showLanding, setShowLanding ] = useState(true);
  const [ landingElementsVisible, setLandingElementsVisible ] = useState(true);
  
  // Detección de inactividad (3 minutos = 180000ms)
  // Solo se activa después de cerrar el landing inicial
  const isInactive = useInactivityDetector(180000, !showLanding);
  const [ showLandingByInactivity, setShowLandingByInactivity ] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      const handleContextMenu = (event) => {
        event.preventDefault();
      };

      // document.addEventListener('contextmenu', handleContextMenu);

      return () => {
        document.removeEventListener('contextmenu', handleContextMenu);
      };
    }
  }, []);

  const componentsMap = {
    homepage: Home,
    lets_talk_strategy: PageKeyDemos,
    //outcomes_for_enterprise: PageOutcomeEnterprise,
    cisco_customer_stories: PageCustomerStories,
    speaking_sessions: PageCiscoSpeakingSessions,
    engagement_zone: PageEngagementZone
  };

  useEffect(() => {
    setSearchIsOpen(false);
  }, []);

  useEffect(() => {
    if (menuActiveKey) {
      const item = content[menuActiveKey];
      
      setTitle(item?.title || "");
      setSubtitle(item?.subtitle || "");
  }
  }, [menuActiveKey]);

  useEffect(() => {
    if (prevLocation && prevLocation.pathname === '/demos' && location.pathname !== '/demos') {
      resetMapOptions();
    }
    setPrevLocation(location);
    
    // Sincronizar menuActiveKey con la ubicación actual
    if (!isLoading && menuItems.length > 0) {
      const currentPath = location.pathname.replace('/', '');
      const currentMenuItem = menuItems.find(item => item.key === currentPath);
      
      if (currentMenuItem && currentMenuItem.key !== menuActiveKey) {
        setMenuActiveKey(currentMenuItem.key);
      }
    }
  }, [location, isLoading, menuItems, menuActiveKey, setMenuActiveKey]);

  // Cargar el contenido JSON al montar el componente
  useEffect(() => {
    loadContent();
  }, [loadContent]);

  useEffect(() => {
    if (!isLoading) {
      // Extraer la key de la ruta actual
      const currentPath = location.pathname.replace('/', '');
      // Buscar si la ruta actual coincide con algún item del menú
      const currentMenuItem = menuItems.find(item => item.key === currentPath);
      
      if (currentMenuItem) {
        // Si la ruta actual coincide con un item del menú, usarla
        setMenuActiveKey(currentMenuItem.key);
      } else {
        // Si no coincide, usar el primer item del menú
        setMenuActiveKey(menuItems[0].key);
      }
    }
  }, [isLoading, location.pathname, menuItems]);

  // Función para mostrar el Landing y resetear el estado de la app
  const showLandingScreen = () => {
    // Cerrar elementos y navegar a homepage después de un delay
    setTimeout(() => {
      closeCarousel();
      setSearchIsOpen(false);
    
      // Navegar a homepage
      if (menuItems.length > 0) {
        navigate(`/${menuItems[0]?.key}`);
      }
    }, 1000);

    // Mostrar Landing
    setLandingElementsVisible(true);
    setShowLandingByInactivity(true);
  };

  // Efecto para mostrar Landing cuando hay inactividad
  useEffect(() => {
    if (isInactive && !showLanding) {
      showLandingScreen();
    }
  }, [isInactive, showLanding]);

  const handleLandingClick = () => {
    // Primero desvanece los elementos internos
    setLandingElementsVisible(false);
    // Después de 0.6s, desvanece toda la capa
    setTimeout(() => {
      setShowLanding(false);
      setShowLandingByInactivity(false);
    }, 600);
  };

  // Handler para doble click/tap en el logo
  const handleLogoDoubleClick = useDoubleClick(() => {
    showLandingScreen();
  });

  const handleButtonClick = (event) => {
    let x, y;

    if (event.touches) {
      // Evento táctil
      x = event.touches[0].clientX;
      y = event.touches[0].clientY;
    } else {
      // Evento de ratón
      x = event.clientX;
      y = event.clientY;
    }
    setCardTouchPos({x, y});

    if (fullScreen) toggleFullScreen();
    else if (carouselIsOpen) closeCarousel();
    else setSearchIsOpen(!searchIsOpen)
  };

  if (isLoading) {
    return <div></div>;
  }

  return (
    <ErrorBoundary>
      <div className="select-none relative min-h-screen flex flex-col w-full bg-cover bg-center bg-no-repeat bg-fixed">
        
        {/* Overlay opcional para mejorar la legibilidad */}
        <div className="absolute inset-0 bg-black bg-opacity-20 pointer-events-none"></div>
        <div className="w-full flex flex-1 relative z-10">
          <Sidebar items={menuItems} />

          <div className="w-full flex-1 min-w-0">

            <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname.includes(menuItems[2].key) ? menuItems[2].key : location.pathname}>
                <Route
                  path="/"
                  element={<Navigate to={`/${menuItems[0]?.key}`} replace />}
                />
                {menuItems.map((item) => {
                  const Component = componentsMap[item.key];
                  if (!Component) {
                    console.error(`No se encontró un componente para la clave: ${item.key}`);
                    return null;
                  }
                  return (
                    <Route
                      key={item.key}
                      path={item.key}
                      element={
                        <ErrorBoundary>
                          <Component />
                        </ErrorBoundary>
                      }
                    />
                  );
                })}
                <Route path={`/${menuItems[2].key}/:key`} element={<PageOutcomeEnterpriseSubpages />} />
              </Routes>
            </AnimatePresence>
          </div>
        </div>
      {/*
      <div 
          className='absolute top-522u left-1/2 text-42u font-light -translate-x-1/2'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          {subtitle}
      </div>
        */}
      <AnimatePresence>
        {carouselIsOpen && carouselType === CAROUSEL_TYPES.KEY_DEMOS && (
          <Carousel
            title={content?.lets_talk_strategy?.title}
            items={content?.lets_talk_strategy?.items}
            initialSlide={carouselIndex}
            itemWidth="159rem"
            itemHeight="73.125rem"
            SlideComponent={CardKeyDemoBig}
            prevButtonText="Previous topic"
            nextButtonText="Next topic"
            section="lets_talk_strategy"
          />
        )}
        {carouselIsOpen && carouselType === CAROUSEL_TYPES.SPEAKERS && (
          <Carousel
            items={content?.speaking_sessions.items}
            initialSlide={carouselIndex}
            SlideComponent={CardSpeaker}
            itemWidth="160.5625rem"
            itemHeight="71.125rem"
            prevButtonText="Previous session"
            nextButtonText="Next session"
            section="speaking_sessions"
          />
        )}
        {carouselIsOpen && carouselType === CAROUSEL_TYPES.STORIES && (
          <Carousel
            items={content?.customer_voices}
            initialSlide={carouselIndex}
            SlideComponent={CardCarousel}
            section="customer_voices"
          />
        )}
        {searchIsOpen && <Search key="search_layer" />}
      </AnimatePresence>

      <MotionLogo 
        className="absolute w-[17.625rem] h-auto left-[8.25rem] top-[5.625rem] z-50 cursor-pointer" 
        animate={{ 
          opacity: fullScreen ? 0 : 1,
          transition: { duration: 0.3, ease: "easeOut" }
        }}
        style={{ pointerEvents: fullScreen ? 'none' : 'auto' }}
        onClick={handleLogoDoubleClick}
      />
      
      <MotionButton
        onClick={(e) => handleButtonClick(e)}
        className="click-effect absolute top-[6.625rem] right-[6.625rem] w-[9.75rem] h-[9.75rem] z-50 justify-center rounded-full focus:outline-none text-white text-[2rem] font-light leading-none flex items-center gap-[3.25rem] overflow-hidden"
        animate={{
          backgroundColor: !searchIsOpen && !fullScreen && !carouselIsOpen ? "#1a2951" : "#ffffff"
        }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        { !searchIsOpen && !fullScreen && !carouselIsOpen
          ? <MotionIconSearch
              key="icon_search"
              initial={{ x: "-200%" }}
              animate={{ x: 0 }}
              exit={{ y: "-200%" }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="w-[3.5rem] h-auto" />
          : <MotionIconClose
              key="icon_close"
              initial={{ x: "200%" }}
              animate={{ x: 0 }}
              exit={{ x: "200%" }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="w-[3.5rem] h-auto"
              color="#1C2C58"
            />
        }
      </MotionButton>

      {/*
      <div className='absolute top-320u left-1/2 text-120u font-extralight -translate-x-1/2 z-30'>{title}</div>
      */}
      <AnimatePresence>
      {!carouselIsOpen && false && (
        <motion.div 
          className='absolute top-522u left-1/2 text-42u font-light -translate-x-1/2'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          {subtitle}
        </motion.div>
      )}
      </AnimatePresence>
      
      {/* Landing Component - Pantalla completa */}
      <AnimatePresence>
        {(showLanding || showLandingByInactivity) && (
          <motion.div
            initial={{ opacity: showLanding ? 1 : 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ 
              duration: showLanding ? 0.5 : 1, 
              ease: showLanding ? "linear" : "easeInOut",
              delay: showLanding ? 0.8 : 0
            }}
            className="fixed inset-0 z-[100] cursor-pointer"
            onClick={handleLandingClick}
          >
            <Landing elementsVisible={landingElementsVisible} />
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </ErrorBoundary>
  );
}

export default App;
